import React, { useCallback, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import DatePicker from '../DatePicker';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import { PALETTE, axisProps, fmtMoney, fmtMoneyShort, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';
import { clinicNow } from '../../utils/clinicTime';

// The day-close view: everything the hospital took in on one day, from both tills - OPD
// consultation fees (net of refunds) and invoice payments - with the token and invoice registers
// underneath so the figures reconcile against the drawer.

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const shiftDay = (iso, days) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toISO(d);
};

const STATUS_TONE = {
  CANCELLED: 'bg-rose-100 text-rose-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  REFERRED: 'bg-purple-100 text-purple-700',
};

const TOKEN_COLUMNS = [
  { key: 'token', label: 'Token', csv: (r) => `${r.tokenPrefix || ''}${r.tokenPrefix ? '-' : ''}${r.tokenNumber ?? ''}`, render: (r) => `${r.tokenPrefix || ''}${r.tokenPrefix ? '-' : ''}${r.tokenNumber ?? ''}` },
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN' },
  { key: 'doctor', label: 'Doctor' },
  { key: 'originalFee', label: 'Fee', align: 'right', render: (r) => fmtMoney(r.originalFee) },
  { key: 'discount', label: 'Discount', align: 'right', render: (r) => fmtMoney(r.discount) },
  { key: 'credit', label: 'Credit Used', align: 'right', render: (r) => fmtMoney(r.credit) },
  { key: 'finalFee', label: 'Final Fee', align: 'right', render: (r) => fmtMoney(r.finalFee) },
  { key: 'refunded', label: 'Refunded', align: 'right', render: (r) => (Number(r.refunded) > 0 ? <span className="text-rose-600">{fmtMoney(r.refunded)}</span> : '—'), csv: (r) => r.refunded },
  { key: 'mode', label: 'Mode' },
  {
    key: 'status', label: 'Status',
    render: (r) => (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_TONE[r.status] || 'bg-gray-100 text-gray-600'}`}>
        {r.status}
      </span>
    ),
  },
];

const INVOICE_COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice #' },
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN' },
  { key: 'subtotal', label: 'Subtotal', align: 'right', render: (r) => fmtMoney(r.subtotal) },
  { key: 'discount', label: 'Discount', align: 'right', render: (r) => fmtMoney(r.discount) },
  { key: 'tax', label: 'Tax', align: 'right', render: (r) => fmtMoney(r.tax) },
  // The billing counter rounds each bill (to the whole rupee by default), so Subtotal - Discount +
  // Tax only reaches Total once the round off is shown too.
  { key: 'rounding', label: 'Round off', align: 'right', render: (r) => (Number(r.rounding) ? fmtMoney(r.rounding) : '—'), csv: (r) => r.rounding ?? 0 },
  { key: 'grandTotal', label: 'Total', align: 'right', render: (r) => fmtMoney(r.grandTotal) },
  { key: 'paid', label: 'Paid', align: 'right', render: (r) => fmtMoney(r.paid) },
  { key: 'pending', label: 'Pending', align: 'right', render: (r) => (Number(r.pending) > 0 ? <span className="text-amber-600 font-semibold">{fmtMoney(r.pending)}</span> : '—'), csv: (r) => r.pending },
  { key: 'mode', label: 'Mode' },
  { key: 'status', label: 'Status' },
];

const DailyCollectionReport = () => {
  const drill = useDrill();
  const [date, setDate] = useState(() => toISO(clinicNow()));
  const fetcher = useCallback(() => reportsAPI.getDailyCollection(date), [date]);
  const { data, loading } = useReport(fetcher);

  const k = data?.kpis || {};
  const kpis = [
    { label: 'Collected Today', value: fmtMoney(k.grandTotal ?? 0), sub: 'OPD + invoices' },
    { label: 'OPD Fees (net)', value: fmtMoney(k.opdNet ?? 0), sub: `${k.visits ?? 0} visits` },
    { label: 'Invoice Payments', value: fmtMoney(k.invoiceCollected ?? 0), sub: `${k.invoicesPaid ?? 0} invoices` },
    { label: 'Refunds Paid Out', value: fmtMoney(k.refundsPaidOut ?? 0), tone: Number(k.refundsPaidOut) > 0 ? 'alert' : undefined },
    { label: 'Discounts Given', value: fmtMoney(k.discountsGiven ?? 0) },
    { label: 'Credit Applied', value: fmtMoney(k.creditApplied ?? 0), sub: 'not fresh cash' },
  ];

  // Both tills merged into one by-mode donut, which is how the drawer is actually counted.
  const byMode = useMemo(() => {
    const merged = {};
    (data?.opdByMode || []).forEach((m) => { merged[m.mode] = (merged[m.mode] || 0) + Number(m.net); });
    (data?.invoiceByMode || []).forEach((m) => { merged[m.mode] = (merged[m.mode] || 0) + Number(m.collected); });
    return Object.entries(merged).filter(([, v]) => v !== 0).map(([mode, value]) => ({ mode, value }));
  }, [data]);

  const byDoctor = (data?.opdByDoctor || []).map((d) => ({ ...d, net: Number(d.net) }));

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-2xl shadow p-4 flex flex-wrap items-center gap-3">
        <button onClick={() => setDate(shiftDay(date, -1))} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50" title="Previous day">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <DatePicker value={date} onChange={(v) => v && setDate(v)} />
        <button onClick={() => setDate(shiftDay(date, 1))} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50" title="Next day">
          <ChevronRight className="w-4 h-4" />
        </button>
        <button onClick={() => setDate(toISO(clinicNow()))} className="px-3 py-1.5 rounded-lg bg-gray-100 text-sm font-medium text-gray-700 hover:bg-gray-200">
          Today
        </button>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartPanel title="Collections by payment mode">
          {byMode.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">Nothing collected on this day</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={byMode} dataKey="value" nameKey="mode" innerRadius={60} outerRadius={95} paddingAngle={2}>
                  {byMode.map((entry, i) => (
                    <Cell key={entry.mode} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
        <ChartPanel title="OPD fees by doctor (net)">
          {byDoctor.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">No OPD visits on this day</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byDoctor} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
                <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
                <YAxis type="category" dataKey="doctor" {...axisProps} width={130} />
                <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="net" radius={[0, 4, 4, 0]} barSize={18}>
              {byDoctor.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title="Token register"
        columns={TOKEN_COLUMNS}
        rows={data?.tokenRegister || []}
        csvName={`token-register-${date}`}
        emptyText="No tokens issued on this day"
        footnote="Cancelled visits stay listed so the register reconciles with the cash drawer."
      />
      <ReportTable
        onRowClick={(r) => drill.open({ type: 'invoice', value: r.invoiceNumber })}
        title="Invoice register"
        columns={INVOICE_COLUMNS}
        rows={data?.invoiceRegister || []}
        csvName={`invoice-register-${date}`}
        emptyText="No invoices paid or finalized on this day"
      />

      <ReportPrintLayout
        title="Daily Collection"
        subtitle={`Date: ${date}`}
        kpis={kpis.map(({ label, value }) => ({ label, value }))}
        tables={[
          { title: 'Token register', columns: TOKEN_COLUMNS, rows: data?.tokenRegister || [] },
          { title: 'Invoice register', columns: INVOICE_COLUMNS, rows: data?.invoiceRegister || [] },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default DailyCollectionReport;
